import { builtInHelpers } from './builtInHelpers.js';

/**
 * 完整模板引擎
 * 支持：变量替换、条件判断、循环、辅助函数调用
 */
class TemplateEngine {
  constructor() {
    this.helpers = { ...builtInHelpers };
  }

  /**
   * 注册自定义辅助函数
   * @param {string} name - 函数名
   * @param {Function} fn - 函数实现
   */
  registerHelper(name, fn) {
    this.helpers[name] = fn;
  }

  /**
   * 渲染模板
   * @param {string} template - 模板字符串
   * @param {Object} context - 上下文数据
   * @returns {string} 渲染结果
   */
  render(template, context) {
    if (!template) return '';

    let result = template;

    // 1. 处理条件块 {{#if condition}}...{{/if}}
    result = this._processConditionals(result, context);

    // 2. 处理循环块 {{#each items}}...{{/each}}
    result = this._processLoops(result, context);

    // 3. 处理辅助函数 {{helperName arg1 arg2}}
    result = this._processHelpers(result, context);

    // 4. 处理简单变量 {{variableName}}
    result = this._processVariables(result, context);

    return result;
  }

  /**
   * 根据分析结果和模板配置生成完整解读
   * @param {Object} analysisResult - GeoAnalyzer 的分析结果
   * @param {Object} templateConfig - templates.json 中的配置
   * @param {string} lang - 语言代码 (zh/en)
   * @returns {string} 完整解读文本
   */
  generateAnalysis(analysisResult, templateConfig, lang = 'zh') {
    const context = this._buildContext(analysisResult, lang);

    let template = null;

    if (templateConfig) {
      // 优先按五行元素匹配模板
      const element = analysisResult?.fengShuiProps?.element;
      if (element && templateConfig.byElement?.[element]?.[lang]) {
        template = templateConfig.byElement[element][lang];
      }
      // 其次按节点类型匹配模板
      else if (
        (analysisResult?.nearestNode?.node?.nodeType || analysisResult?.nearestNode?.node?.type) &&
        templateConfig.byNodeType?.[
          analysisResult.nearestNode.node.nodeType || analysisResult.nearestNode.node.type
        ]?.[lang]
      ) {
        template =
          templateConfig.byNodeType[
            analysisResult.nearestNode.node.nodeType || analysisResult.nearestNode.node.type
          ][lang];
      }
      // 最后使用默认模板
      else if (templateConfig.default?.[lang]) {
        template = templateConfig.default[lang];
      } else if (templateConfig.default?.['zh']) {
        template = templateConfig.default['zh'];
      }
    }

    if (!template) {
      return this._generateFallbackText(analysisResult, lang);
    }

    return this.render(template, context);
  }

  _buildContext(analysisResult, lang) {
    const { selectedPoint, subzone, planningArea, fengShuiProps, nearestNode, nodesInRange, nearestVein } =
      analysisResult || {};

    const node = nearestNode?.node || null;
    const positioningRaw = node?.positioning ?? node?.fengShuiPositioning ?? '';
    const positioning = Array.isArray(positioningRaw) ? positioningRaw.join('、') : positioningRaw;
    const mainAreasRaw = node?.mainAreas ?? [];
    const industriesRaw = node?.industries ?? node?.recommendedIndustries ?? [];

    return {
      // 基础信息
      lat: selectedPoint?.lat?.toFixed(4) || '',
      lng: selectedPoint?.lng?.toFixed(4) || '',
      lang,

      // 区域信息
      regionName: subzone?.nameZh || '未知区域',
      regionNameEn: subzone?.nameEn || 'Unknown Area',
      planningArea: planningArea?.nameZh || '',

      // 风水属性
      element: fengShuiProps?.element || '未知',
      elementEn: fengShuiProps?.elementEn || 'Unknown',
      direction: fengShuiProps?.direction || '未知',
      directionEn: fengShuiProps?.directionEn || 'Unknown',
      flyingStar: fengShuiProps?.flyingStar || '未知',
      colorName: fengShuiProps?.colorName || '',
      prosperousIndustries: fengShuiProps?.prosperousIndustries || [],

      // 节点信息
      nearestNodeName: nearestNode?.node?.nameZh || '无',
      nearestNodeNameEn: nearestNode?.node?.nameEn || 'None',
      nearestNodeDistance: nearestNode?.distance || 0,
      nearestNodeInRange: Array.isArray(nodesInRange) && nodesInRange.length > 0,
      nodeElement: node?.element || '',
      nodePositioning: positioning || '',
      nodeMainAreas: Array.isArray(mainAreasRaw) ? mainAreasRaw.join('、') : mainAreasRaw || '',
      nodeIndustries: Array.isArray(industriesRaw) ? industriesRaw.join('、') : industriesRaw || '',
      influenceRadius: node?.influenceRadius ?? node?.influenceRadiusMeters ?? 0,
      distance: nearestNode?.distance || 0,

      // 走向信息
      nearestVeinName: nearestVein?.vein?.nameZh || '',
      nearestVeinDistance: nearestVein?.distance || 0,

      // 辅助函数
      ...this.helpers,
    };
  }

  _processConditionals(template, context) {
    const ifRegex = /\{\{#if\s+([^}]+)\}\}([\s\S]*?)\{\{\/if\}\}/g;
    return template.replace(ifRegex, (match, condition, content) => {
      const value = this._resolveValue(condition.trim(), context);
      return value ? content : '';
    });
  }

  _processLoops(template, context) {
    const eachRegex = /\{\{#each\s+([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g;
    return template.replace(eachRegex, (match, arrayName, itemTemplate) => {
      const array = this._resolveValue(arrayName.trim(), context);
      if (!Array.isArray(array)) return '';
      return array
        .map((item, index) => {
          const itemContext = { ...context, this: item, index };
          return this.render(itemTemplate, itemContext);
        })
        .join('');
    });
  }

  _processHelpers(template, context) {
    const helperRegex = /\{\{(\w+)\s+([^}]+)\}\}/g;
    return template.replace(helperRegex, (match, helperName, args) => {
      const helper = this.helpers[helperName];
      if (!helper) return match;

      const trimmedArgs = args.trim();
      const resolvedArgs = trimmedArgs
        ? trimmedArgs.split(/\s+/).map((arg) => this._resolveArg(arg, context))
        : [];
      const result = helper(...resolvedArgs);
      return result !== undefined && result !== null ? String(result) : '';
    });
  }

  _resolveArg(arg, context) {
    if (arg === 'true') return true;
    if (arg === 'false') return false;
    if (arg === 'null') return null;
    if (arg === 'undefined') return undefined;

    const num = Number(arg);
    if (!Number.isNaN(num) && arg !== '') return num;

    const resolved = this._resolveValue(arg, context);
    return resolved !== undefined ? resolved : arg;
  }

  _processVariables(template, context) {
    const varRegex = /\{\{([^#/](?:(?!\}\}).)*)\}\}/g;
    const afterDouble = template.replace(varRegex, (match, path) => {
      const trimmedPath = path.trim();
      const value = this._resolveValue(trimmedPath, context);
      return value !== undefined && value !== null ? String(value) : '';
    });

    const singleVarRegex = /\{([a-zA-Z0-9_.]+)\}/g;
    return afterDouble.replace(singleVarRegex, (match, path) => {
      const value = this._resolveValue(path.trim(), context);
      return value !== undefined && value !== null ? String(value) : match;
    });
  }

  _resolveValue(path, context) {
    const keys = path.split('.');
    let value = context;
    for (const key of keys) {
      if (value === null || value === undefined) return undefined;
      value = value[key];
    }
    return value;
  }

  _generateFallbackText(analysisResult, lang) {
    const subzoneName = analysisResult?.subzone?.nameZh || '未知区域';
    const subzoneNameEn = analysisResult?.subzone?.nameEn || 'Unknown Area';

    if (lang === 'zh') {
      return `您选择的位置位于 ${subzoneName} 附近。`;
    }
    return `You selected a location near ${subzoneNameEn}.`;
  }
}

export { TemplateEngine };
export const templateEngine = new TemplateEngine();
